import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  BarChart3,
  Users,
  FileText,
  Video,
  Scissors,
  Share2,
  TrendingUp,
  User,
  LogOut,
  Shield,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { T, FONT_SANS, FADE_FAST, SPRING_PANEL, SPRING_PRESS } from '../theme/radar';

interface NavEntry {
  name: string;
  href: string;
  icon: LucideIcon;
  /** Voce visibile solo agli admin (come i link del pannello account). */
  adminOnly?: boolean;
}

interface NavSection {
  /** Senza titolo il gruppo parte subito sotto il logo, come «Da gestire» nel Radar. */
  title?: string;
  items: NavEntry[];
}

/** Gruppi come le NavSection del Radar: la coda da gestire in cima, il lavoro
    quotidiano sotto, la configurazione in fondo. */
const sections: NavSection[] = [
  {
    items: [
      { name: 'Da gestire', href: '/dashboard', icon: BarChart3 },
      { name: 'Clienti', href: '/clienti', icon: Users },
    ],
  },
  {
    title: 'Operatività',
    items: [
      { name: 'Copywriting', href: '/copywriter', icon: FileText },
      { name: 'Riprese', href: '/videomaker', icon: Video },
      { name: 'Editing', href: '/editing', icon: Scissors },
      { name: 'Calendario', href: '/calendario-editoriale', icon: CalendarDays },
      { name: 'Pubblicazione', href: '/publisher', icon: Share2 },
    ],
  },
  {
    title: 'Configurazione',
    items: [
      { name: 'Gestione Ruoli', href: '/admin-assignments', icon: Shield, adminOnly: true },
      // Prestazioni del mese e tempi medi di lavoro (ex "Maggiori dettagli" di Da gestire)
      { name: 'Analisi', href: '/analisi', icon: TrendingUp, adminOnly: true },
    ],
  },
];

/** Larghezze del Radar: menù aperto e rail a sole icone. */
export const SIDEBAR_WIDTH = 216;
export const SIDEBAR_RAIL_WIDTH = 60;

/**
 * Nell'animazione in larghezza il colore e l'altezza della barretta seguono
 * Fade.fast; la larghezza stessa segue la molla `panel` via framer-motion.
 */
const TRANSITION = `background-color ${FADE_FAST}, color ${FADE_FAST}, height ${FADE_FAST}, opacity ${FADE_FAST}`;

/** Etichetta di sezione: labelSmall del Radar (10.5px, maiuscolo, spaziato). */
const sectionLabelStyle: React.CSSProperties = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: 1.1,
  textTransform: 'uppercase',
  color: T.inkFaint,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
};

interface SidebarProps {
  onNavigate?: () => void;
  isMobile?: boolean;
  /** Rail a sole icone (solo desktop). Al passaggio del mouse si riapre da sola. */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** Avvisa il layout quando il rail chiuso si riapre sotto il puntatore,
      così il contenuto si sposta con lui come nella Row del Radar. */
  onPeekChange?: (peeking: boolean) => void;
}

/**
 * Tooltip che compare solo con il puntatore (HoverTooltip del Radar): sul
 * touch non esiste, perché un tooltip appeso sopra il menù è peggio
 * dell'etichetta mancante.
 */
const HoverTooltip: React.FC<{ label: string; show: boolean; children: React.ReactNode }> = ({
  label,
  show,
  children,
}) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
      <AnimatePresence>
        {show && hovered && (
          <motion.div
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -4 }}
            transition={{ duration: 0.13, ease: [0.33, 1, 0.68, 1] }}
            className="pointer-events-none absolute top-1/2 -translate-y-1/2 whitespace-nowrap z-50"
            style={{
              left: SIDEBAR_RAIL_WIDTH - 4,
              padding: '4px 8px',
              borderRadius: 4,
              fontSize: 12,
              color: T.surface,
              backgroundColor: T.ink,
            }}
          >
            {label}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/** Voce di menù nello stile di _SidebarTile del Radar. */
const NavTile: React.FC<{
  to: string;
  label: string;
  icon: LucideIcon;
  active: boolean;
  expanded: boolean;
  onClick: () => void;
}> = ({ to, label, icon: Icon, active, expanded, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const foreground = active || hovered ? T.ink : T.inkMuted;
  const background = active
    ? T.surfaceHigh
    : hovered
      ? 'rgba(240,241,244,0.45)'
      : 'transparent';

  const tile = (
    <motion.div whileTap={{ scale: 0.97 }} transition={SPRING_PRESS}>
      <Link
        to={to}
        onClick={onClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="flex items-center touch-manipulation"
        style={{
          height: 36,
          margin: '1px 8px',
          borderRadius: 6,
          backgroundColor: background,
          color: foreground,
          transition: TRANSITION,
          overflow: 'hidden',
        }}
      >
        {/* Barretta gialla 2px sul bordo di entrata: sale a 16px quando attiva */}
        <span
          aria-hidden="true"
          style={{
            width: 2,
            height: active ? 16 : 0,
            borderRadius: 1,
            backgroundColor: T.accent,
            flexShrink: 0,
            transition: TRANSITION,
          }}
        />
        <span style={{ width: 12, flexShrink: 0 }} />
        <Icon size={16} strokeWidth={1.75} style={{ color: foreground, flexShrink: 0 }} />
        {/* L'etichetta resta nel DOM e viene tagliata dal clip mentre il rail
            si stringe: così il testo non salta, si chiude col pannello. */}
        <span style={{ width: 12, flexShrink: 0 }} />
        <span
          style={{
            fontSize: 13,
            fontWeight: active ? 600 : 400,
            color: foreground,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            flex: 1,
            opacity: expanded ? 1 : 0,
            transition: TRANSITION,
          }}
        >
          {label}
        </span>
      </Link>
    </motion.div>
  );

  return (
    <HoverTooltip label={label} show={!expanded}>
      {tile}
    </HoverTooltip>
  );
};

/** Bottone icona del piede (IconButtonTheme del Radar: inkMuted, raggio 6). */
const FooterIconButton: React.FC<{
  label: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}> = ({ label, onClick, danger = false, children }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <motion.button
      type="button"
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      whileTap={{ scale: 0.97 }}
      transition={SPRING_PRESS}
      className="inline-flex items-center justify-center"
      title={label}
      aria-label={label}
      style={{
        width: 32,
        height: 32,
        borderRadius: 6,
        color: danger ? T.danger : hovered ? T.ink : T.inkMuted,
        backgroundColor: hovered
          ? danger
            ? 'rgba(220,38,38,0.08)'
            : 'rgba(240,241,244,0.45)'
          : 'transparent',
        transition: TRANSITION,
      }}
    >
      {children}
    </motion.button>
  );
};

const Sidebar: React.FC<SidebarProps> = ({
  onNavigate,
  isMobile = false,
  collapsed = false,
  onToggleCollapsed,
  onPeekChange,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  // Vero mentre il puntatore è sopra il rail chiuso: lo riapre senza cambiare
  // lo stato scelto dall'utente (il "peek" del Radar).
  const [peeking, setPeeking] = useState(false);
  const expanded = isMobile || !collapsed || peeking;

  const setPeek = (value: boolean) => {
    setPeeking(value);
    onPeekChange?.(value);
  };

  const handleNavClick = () => {
    if (onNavigate) {
      onNavigate();
    }
  };

  const handleLogout = () => {
    logout();
    if (onNavigate) {
      onNavigate();
    }
  };

  return (
    <motion.div
      className="sidebar-radar flex h-full flex-col"
      initial={false}
      animate={{ width: isMobile ? '100%' : expanded ? SIDEBAR_WIDTH : SIDEBAR_RAIL_WIDTH }}
      transition={SPRING_PANEL}
      onMouseEnter={collapsed && !isMobile ? () => setPeek(true) : undefined}
      onMouseLeave={collapsed && !isMobile ? () => setPeek(false) : undefined}
      style={{
        backgroundColor: T.sunken,
        borderRight: `1px solid ${T.borderSoft}`,
        color: T.ink,
        fontFamily: FONT_SANS,
        letterSpacing: 0,
        overflow: 'hidden',
      }}
    >
      {/* Marchio: il wordmark PNG è bianco con alpha, qui viene tinto d'inchiostro
          come fa il Radar con ColorFilter.srcIn. Chiuso, il rail è largo
          quanto basta per un'icona; aperto c'è spazio perché si legga. */}
      <div
        style={{
          padding: expanded ? '24px 8px 16px 16px' : '24px 8px 16px 8px',
          transition: `padding ${FADE_FAST}`,
        }}
      >
        <div className={`flex items-center ${expanded ? 'justify-between' : 'justify-center'}`}>
          <Link
            to="/dashboard"
            onClick={handleNavClick}
            className="logo-container inline-flex items-center"
            aria-label="Vai alla dashboard"
            style={{ maxWidth: '100%' }}
          >
            <motion.img
              src="/bymarke-wordmark-white.png"
              alt="ByMarke"
              className="w-auto select-none"
              animate={{ height: expanded ? 40 : 26 }}
              transition={SPRING_PANEL}
              style={{
                maxWidth: '100%',
                filter: 'brightness(0)',
                objectFit: 'contain',
                objectPosition: 'left center',
              }}
              draggable={false}
            />
          </Link>

          {isMobile && (
            <FooterIconButton label="Chiudi menu" onClick={() => onNavigate?.()}>
              <X size={18} strokeWidth={1.75} />
            </FooterIconButton>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex flex-1 flex-col overflow-y-auto overflow-x-hidden" style={{ paddingTop: 8 }}>
        {sections.map((section) => {
          const items = section.items.filter((item) => !item.adminOnly || user?.role === 'admin');
          if (items.length === 0) return null;
          return (
            <React.Fragment key={section.title ?? items[0].href}>
              {/* Chiuso, al posto dell'etichetta resta un filetto: il gruppo
                  sopravvive anche senza il testo (come _SectionLabel del Radar). */}
              {section.title && (
                expanded ? (
                  <div style={{ ...sectionLabelStyle, padding: '16px 16px 8px' }}>{section.title}</div>
                ) : (
                  <div style={{ margin: '12px 16px 8px', height: 1, backgroundColor: T.borderSoft }} />
                )
              )}
              {items.map((item) => (
                <NavTile
                  key={item.name}
                  to={item.href}
                  label={item.name}
                  icon={item.icon}
                  active={location.pathname === item.href}
                  expanded={expanded}
                  onClick={handleNavClick}
                />
              ))}
              <div style={{ height: 12 }} />
            </React.Fragment>
          );
        })}
      </nav>

      {/* Piede: trattenuto mentre il rail è chiuso (a 60px i comandi sarebbero
          detriti); il passaggio del mouse lo riporta con il resto del menù.
          Solo icone, come la riga comandi del Radar: profilo, esci, riduci. */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="sidebar-footer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.13, ease: [0.33, 1, 0.68, 1] }}
            className="flex items-center"
            style={{ borderTop: `1px solid ${T.borderSoft}`, padding: 8, minWidth: SIDEBAR_WIDTH }}
          >
            <FooterIconButton label="Il Mio Profilo" onClick={() => { navigate('/profile'); handleNavClick(); }}>
              <User size={18} strokeWidth={1.75} />
            </FooterIconButton>
            <FooterIconButton label="Esci" onClick={handleLogout} danger>
              <LogOut size={18} strokeWidth={1.75} />
            </FooterIconButton>
            <div className="flex-1" />
            {!isMobile && onToggleCollapsed && (
              <FooterIconButton
                label={collapsed ? 'Espandi il menù' : 'Riduci il menù'}
                onClick={() => {
                  setPeek(false);
                  onToggleCollapsed();
                }}
              >
                {collapsed ? (
                  <ChevronRight size={18} strokeWidth={1.75} />
                ) : (
                  <ChevronLeft size={18} strokeWidth={1.75} />
                )}
              </FooterIconButton>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default Sidebar;
